import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PlayCard = ({ play }) => {
  const navigate = useNavigate();

  const getDifficultyColor = (difficulty) => {
    const colors = {
      beginner: 'text-success bg-success/10',
      intermediate: 'text-warning bg-warning/10',
      advanced: 'text-error bg-error/10'
    };
    return colors?.[difficulty] || colors?.beginner;
  };

  const getDifficultyIcon = (difficulty) => {
    const icons = {
      beginner: 'Leaf',
      intermediate: 'Flame',
      advanced: 'Zap'
    };
    return icons?.[difficulty] || 'Leaf';
  };

  return (
    <div className="bg-card rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-border">
      <div className="relative h-48 overflow-hidden">
        <Image 
          src={play?.coverImage} 
          alt={play?.coverImageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(play?.difficulty)} flex items-center gap-1`}>
            <Icon name={getDifficultyIcon(play?.difficulty)} size={14} />
            {play?.difficulty?.charAt(0)?.toUpperCase() + play?.difficulty?.slice(1)}
          </span>
        </div>
        {play?.isNew && (
          <div className="absolute top-3 left-3">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-accent text-accent-foreground">
              New
            </span>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="text-xl font-bold text-card-foreground mb-2">
          {play?.title?.replace(/\w\S*/g, (word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase())}
        </h3>
        <div className="flex items-center gap-3 mb-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Icon name="BookOpen" size={16} />
            {play?.totalActs} Acts
          </span>
          <span className="flex items-center gap-1">
            <Icon name="Layout" size={16} />
            {play?.totalScenes} Scenes
          </span>
          <span className="flex items-center gap-1">
            <Icon name="AlignLeft" size={16} />
            {play?.totalLines} Lines
          </span>
        </div>

        <div className="flex items-center gap-3 mb-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Icon name="Users" size={16} />
            {play?.characterCount} Characters
          </span>
          <span className="flex items-center gap-1">
            <Icon name="Clock" size={16} />
            {play?.estimatedTime}
          </span>
        </div>

        {play?.completionPercentage > 0 && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-semibold text-primary">{play?.completionPercentage}%</span>
            </div>
            <div className="w-full bg-border rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-primary to-secondary h-full rounded-full transition-all duration-500"
                style={{ width: `${play?.completionPercentage}%` }}
              />
            </div>
          </div>
        )}

        {play?.earnedBadges?.length > 0 && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm text-muted-foreground">Badges:</span>
            <div className="flex gap-1">
              {play?.earnedBadges?.slice(0, 3)?.map((badge, index) => (
                <div key={index} className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center" title={badge}>
                  <Icon name="Award" size={14} color="var(--color-secondary-foreground)" />
                </div>
              ))}
              {play?.earnedBadges?.length > 3 && (
                <span className="text-xs text-muted-foreground ml-1">+{play?.earnedBadges?.length - 3}</span>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <Button 
            variant="default" 
            fullWidth
            iconName={play?.completionPercentage > 0 ? "PlayCircle" : "BookOpen"}
            iconPosition="left"
            onClick={() => navigate(`/play-reading-interface?playId=${play?.id}`)}
          >
            {play?.completionPercentage > 0 ? 'Continue' : 'Start Reading'}
          </Button>
          <Button 
            variant="outline"
            iconName="Brain"
            onClick={() => navigate(`/quiz-interface?playId=${play?.id}`)}
          >
            Quiz
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlayCard;